import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './GuestList.css';
import './VendorManagement.css';

interface Vendor {
    _id: string;
    name: string;
    category: string;
    contactNumber: string;
}

const CATEGORIES = [
    'Mehandi', 'Phool', 'Band Baja', 'Catering', 'Lighting',
    'Photography', 'Decoration', 'Venue', 'Makeup Artist', 'Other'
];

const VendorManagement: React.FC = () => {
    const { accessToken } = useAuth();
    const history = useHistory();
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

    // Form State
    const [name, setName] = useState('');
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [contactNumber, setContactNumber] = useState('');

    const fetchVendors = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/vendors`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            const data = await response.json();
            if (response.ok) {
                setVendors(data.vendors);
            }
        } catch (err) {
            console.error('Error fetching vendors');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken) fetchVendors();
    }, [accessToken]);

    const handleOpenModal = (vendor: Vendor | null = null) => {
        if (vendor) {
            setEditingVendor(vendor);
            setName(vendor.name);
            setCategory(vendor.category);
            setContactNumber(vendor.contactNumber);
        } else {
            setEditingVendor(null);
            setName('');
            setCategory(CATEGORIES[0]);
            setContactNumber('');
        }
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const url = editingVendor
            ? `${API_BASE_URL}/api/vendors/${editingVendor._id}`
            : `${API_BASE_URL}/api/vendors`;

        const method = editingVendor ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({ name, category, contactNumber })
            });

            if (response.ok) {
                setShowModal(false);
                fetchVendors();
            } else {
                alert('Action failed');
            }
        } catch (err) {
            alert('Server error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to remove this vendor?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/vendors/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response.ok) fetchVendors();
        } catch (err) {
            alert('Error deleting vendor');
        }
    };

    if (loading) return <div className="admin-dashboard">Loading...</div>;

    return (
        <div className="admin-dashboard vendor-mgmt-container">
            <div className="vendor-header">
                <div className="title-area">
                    <h1>Vendor Directory</h1>
                    <p>Manage your professional wedding service providers</p>
                </div>
                <button className="add-vendor-btn" onClick={() => handleOpenModal()}>
                    ➕ Add New Vendor
                </button>
            </div>

            <div className="vendor-grid">
                {vendors.length === 0 ? (
                    <div className="no-vendors">
                        <div className="empty-icon">🏪</div>
                        <h2>No vendors added yet</h2>
                        <p>Click the button above to start your vendor list.</p>
                    </div>
                ) : (
                    vendors.map((vendor) => (
                        <div key={vendor._id} className="vendor-card">
                            <div className="vendor-card-header">
                                <span className="category-tag-premium">{vendor.category}</span>
                                <div className="vendor-actions-mini">
                                    <button className="vendor-btn-mini" onClick={() => handleOpenModal(vendor)} title="Edit">✏️</button>
                                    <button className="vendor-btn-mini delete" onClick={() => handleDelete(vendor._id)} title="Delete">🗑️</button>
                                </div>
                            </div>

                            <div className="vendor-card-body">
                                <h3 className="vendor-name-premium">{vendor.name}</h3>
                                <div className="vendor-contact-premium">
                                    <span className="contact-icon">📱</span>
                                    <span className="contact-text">{vendor.contactNumber}</span>
                                </div>
                            </div>

                            <div className="vendor-card-footer">
                                <button className="vendor-call-btn" onClick={() => window.open(`tel:${vendor.contactNumber}`)}>
                                    Call Vendor
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="vendor-modal glass-panel">
                        <h2>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Vendor Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Royal Catering Services"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    required
                                >
                                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Contact Number</label>
                                <input
                                    type="text"
                                    value={contactNumber}
                                    onChange={(e) => setContactNumber(e.target.value)}
                                    placeholder="+91 XXXXX XXXXX"
                                    required
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="confirm-btn">
                                    {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorManagement;

